import {
  Box2,
  Color,
  Group,
  Mesh,
  MeshBasicMaterial,
  Path,
  Shape,
  ShapeGeometry,
  Vector2,
  Vector3,
} from 'three';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry';

import { renderOrders } from '../../render-orders';
import { degreesToRadians, radiansToDegrees } from '../../utilities/math-helpers';
import { PickingSphere } from '../picking-sphere';

/**
 * The CAD drawing report's frame: the page's map area on the ground.
 */
export type ReportFrameState = {
  /** In the project's coordinate system (local coordinates plus the projection origo). */
  center: Vector2;
  /** In the coordinate system's unit. */
  width: number;
  height: number;
  /** Radians, counter-clockwise from north-up, from 0 to 2π. */
  rotation: number;
};

/** The part of the frame under the pointer: the center moves it, the edges turn it. */
export type ReportFramePart = 'center' | 'edge';

const FULL_TURN = 2 * Math.PI;
const RIGHT_ANGLE = Math.PI / 2;
/** How close to a right angle a rotation snaps to it. */
const RIGHT_ANGLE_SNAP = degreesToRadians(2);

/**
 * Snaps a rotation to whole degrees, or to 0°, 90°, 180° or 270° when it's close to them, and
 * normalizes it to 0 to 2π.
 */
export function snapFrameRotation(rotation: number): number {
  const normalized = ((rotation % FULL_TURN) + FULL_TURN) % FULL_TURN;
  const rightAngle = Math.round(normalized / RIGHT_ANGLE) * RIGHT_ANGLE;
  const snapped =
    Math.abs(normalized - rightAngle) <= RIGHT_ANGLE_SNAP
      ? rightAngle
      : degreesToRadians(Math.round(radiansToDegrees(normalized)));

  // 360° is 0°.
  return snapped >= FULL_TURN - 1e-9 ? 0 : snapped;
}

/** The point in the frame's own coordinates: from its center, along its unrotated axes. */
function toFrameCoordinates(point: Vector2, frame: ReportFrameState) {
  return point.clone().sub(frame.center).rotateAround(new Vector2(), -frame.rotation);
}

/** The north-up box around the frame, which covers all of it when it's rotated. */
export function getReportFrameBounds(frame: ReportFrameState): Box2 {
  const cos = Math.abs(Math.cos(frame.rotation));
  const sin = Math.abs(Math.sin(frame.rotation));
  const halfSize = new Vector2(
    (frame.width * cos + frame.height * sin) / 2,
    (frame.width * sin + frame.height * cos) / 2,
  );

  return new Box2(frame.center.clone().sub(halfSize), frame.center.clone().add(halfSize));
}

/**
 * Gets the part of the frame at the point, if any. The tolerance is how far from the center or an
 * edge the point may be, in the coordinate system's unit.
 */
export function pickReportFrame(
  point: Vector2,
  frame: ReportFrameState,
  tolerance: number,
): ReportFramePart | undefined {
  if (point.distanceTo(frame.center) <= tolerance) {
    return 'center';
  }

  // How far outside each pair of edges the point is, negative inside.
  const local = toFrameCoordinates(point, frame);
  const outsideX = Math.abs(local.x) - frame.width / 2;
  const outsideY = Math.abs(local.y) - frame.height / 2;
  const nearEdge = outsideX <= tolerance && outsideY <= tolerance;
  const awayFromInside = Math.max(outsideX, outsideY) >= -tolerance;

  return nearEdge && awayFromInside ? 'edge' : undefined;
}

/** The angle of the point around the frame's center, for turning it. */
export function getAngleAroundFrame(point: Vector2, frame: ReportFrameState): number {
  return Math.atan2(point.y - frame.center.y, point.x - frame.center.x);
}

/** How far out the dimming mask reaches from the frame, enough to cover the view when zoomed out. */
const MASK_EXTENT = 1e6;
const OUTLINE_WIDTH_PX = 2;
const CENTER_HANDLE_PX = 16;
const EDGE_HANDLE_PX = 12;

const accent = new Color(0xfed530);
const hovered = new Color(0x6385f1);

/**
 * Draws the frame in local coordinates: its outline, a mask that dims the view outside it, a handle
 * in the center to move it and handles at the middle of its edges to show that they turn it.
 */
export class ReportFrame extends Group {
  private readonly outline = new LineSegments2(
    new LineSegmentsGeometry(),
    new LineMaterial({
      color: accent,
      linewidth: OUTLINE_WIDTH_PX,
      depthTest: false,
      depthWrite: false,
      transparent: true,
    }),
  );

  private readonly mask = new Mesh(
    new ShapeGeometry(new Shape()),
    new MeshBasicMaterial({
      color: 0x000000,
      opacity: 0.35,
      depthTest: false,
      depthWrite: false,
      transparent: true,
    }),
  );

  private readonly centerHandle = new PickingSphere(accent.clone(), CENTER_HANDLE_PX, 'round');
  private readonly edgeHandles = new PickingSphere(accent.clone(), EDGE_HANDLE_PX, 'circle');
  private size = new Vector2();

  constructor() {
    super();

    this.outline.renderOrder = renderOrders.reportFrame.outline;
    this.mask.renderOrder = renderOrders.reportFrame.mask;
    this.centerHandle.renderOrder = renderOrders.reportFrame.handles;
    this.edgeHandles.renderOrder = renderOrders.reportFrame.handles;

    for (const object of [this.outline, this.mask, this.centerHandle, this.edgeHandles]) {
      object.frustumCulled = false;
      this.add(object);
    }

    this.centerHandle.setNonIndexedVertices([new Vector3()]);
  }

  /**
   * Places the frame. The center is in local coordinates.
   */
  update(frame: Pick<ReportFrameState, 'width' | 'height' | 'rotation'>, center: Vector2) {
    this.position.setX(center.x).setY(center.y);
    this.rotation.z = frame.rotation;

    if (this.size.x !== frame.width || this.size.y !== frame.height) {
      this.size.set(frame.width, frame.height);
      this.updateGeometry();
    }

    this.updateMatrixWorld();
  }

  /** Highlights the part under the pointer. */
  setHovered(part: ReportFramePart | undefined) {
    this.centerHandle.setColor(part === 'center' ? hovered : accent);
    this.edgeHandles.setColor(part === 'edge' ? hovered : accent);
    this.outline.material.color = part === 'edge' ? hovered : accent;
  }

  /**
   * Keeps the outline its width in pixels, and the frame between the camera's near and far planes.
   */
  onAnimate(canvasWidth: number, canvasHeight: number, height: number) {
    this.outline.material.resolution.set(canvasWidth, canvasHeight);
    this.position.z = height;
    this.updateMatrixWorld();
  }

  private updateGeometry() {
    const x = this.size.x / 2;
    const y = this.size.y / 2;
    const corners = [
      [-x, -y],
      [x, -y],
      [x, y],
      [-x, y],
    ];

    this.outline.geometry.setPositions(
      corners.flatMap(([cx, cy], i) => {
        const [nx, ny] = corners[(i + 1) % corners.length];
        return [cx, cy, 0, nx, ny, 0];
      }),
    );

    const shape = new Shape()
      .moveTo(-MASK_EXTENT, -MASK_EXTENT)
      .lineTo(MASK_EXTENT, -MASK_EXTENT)
      .lineTo(MASK_EXTENT, MASK_EXTENT)
      .lineTo(-MASK_EXTENT, MASK_EXTENT);
    shape.holes.push(new Path().moveTo(-x, -y).lineTo(-x, y).lineTo(x, y).lineTo(x, -y));
    this.mask.geometry.dispose();
    this.mask.geometry = new ShapeGeometry(shape);

    this.edgeHandles.setNonIndexedVertices([
      new Vector3(0, -y, 0),
      new Vector3(x, 0, 0),
      new Vector3(0, y, 0),
      new Vector3(-x, 0, 0),
    ]);
  }
}
